import clsx from "clsx";
import { getLength, getPointAtLength } from "@remotion/paths";
import { useEffect, useState, useMemo } from "react";
import * as d3 from "d3";
import { ChartHeader } from "@/components/ChartHeader";
import { ColorLegend } from "@/components/ColorLegend";
import { useTooltip } from "@/components/Tooltip";
import { Toggle } from "@/components/Toggle";
import { NoData } from "@/components/NoData";
import {
  formatDisplayList,
  formatMoney,
  formatMoneySr,
  getEconomyDisplayName,
  getSectorDisplayName,
  getSubSectorDisplayName,
} from "@/utils/format";
import sectorOptions from "@/config/trade-network/sectors";
import economies from "@/config/economies";
import economyRegions from "@/config/economy-regions";
import regions from "@/config/regions";
import colors from "@/config/colors";
import regionColors from "@/config/region-colors";
import coord from "@/config/coord";
import { useFilter } from "@/hooks/useFilter";
import type { DataRow } from "../App";
import styles from "./styles.module.scss";

// Set up SVG
const width = 1200;
const height = 650;
const maxCircleR = 60;
const padding = maxCircleR + 20;

type Coordinates = {
  x: number;
  y: number;
};

type CoordinatesWithEconomyCode = Coordinates & {
  economyCode: keyof typeof coord;
};

// Create an SVG <path> that curves between two coordinate pairs
const createPathString = (start: Coordinates, end: Coordinates) => {
  const curveFactor = 1.5; // higher is straighter
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const dr = Math.sqrt(dx * dx + dy * dy) * curveFactor;
  const pathString = `M ${start.x} ${start.y} A${dr} ${dr} 0 0, 0 ${end.x} ${end.y}`;
  return pathString;
};

const toggleOptions = [
  { value: "i", label: "Global imports" },
  { value: "e", label: "Global exports" },
];

// Construct legend data
const legendItems = Object.entries(regionColors).map(
  ([regionCode, colorName]) => ({
    label: regions[regionCode as keyof typeof regions],
    colorName,
  })
);

const isCoordEconomyCode = (code: string): code is keyof typeof coord =>
  code in coord;

interface GeoNetworkProps {
  data: DataRow[];
  worldData: DataRow[];
  sector: (typeof sectorOptions)[number];
  subSector: string;
  year: string;
  topFlowsPercent: number;
}

export function GeoNetworkChart({
  data,
  worldData,
  sector,
  subSector,
  year,
  topFlowsPercent,
}: GeoNetworkProps) {
  const [animatedNodes, setAnimatedNodes] = useState<
    CoordinatesWithEconomyCode[]
  >([]);
  const [animationTickCount, setAnimationTickCount] = useState(0);

  const { value: toggle, onChange: onToggleChange } = useFilter(
    "toggle",
    toggleOptions.map((o) => o.value)
  );

  const displayEconomyCodes = useMemo(() => {
    const economyCodes = new Set<keyof typeof coord>();
    data.forEach((flow) => {
      if (isCoordEconomyCode(flow["Reporter code"])) {
        economyCodes.add(flow["Reporter code"]);
      }
      if (isCoordEconomyCode(flow["Partner code"])) {
        economyCodes.add(flow["Partner code"]);
      }
    });

    return Array.from(economyCodes.values());
  }, [data]);

  const importCircleSizes = displayEconomyCodes.reduce<{
    [key: string]: number;
  }>(
    (sizes, economyCode) => ({
      ...sizes,
      [economyCode]:
        worldData.find(
          (flow) =>
            flow["Reporter code"] === economyCode &&
            flow["Partner code"] === "WLD"
        )?.Value ?? 0,
    }),
    {}
  );
  const exportCircleSizes = displayEconomyCodes.reduce<{
    [key: string]: number;
  }>(
    (sizes, economyCode) => ({
      ...sizes,
      [economyCode]:
        worldData.find(
          (flow) =>
            flow["Reporter code"] === "WLD" &&
            flow["Partner code"] === economyCode
        )?.Value ?? 0,
    }),
    {}
  );

  const { showTooltip, hideTooltip, tooltipVisible, tooltipData } = useTooltip<
    | ({
        type: "flow";
        relatedEconomies?: string[];
      } & (typeof data)[number])
    | {
        type: "economy";
        relatedEconomies?: string[];
        code: keyof typeof economies;
      }
  >();

  // This needs to be memoized because d3 will mutate elements in this array to
  // update their position in the simulation below. If we create a new array at
  // every render, circles won't move.
  const geoProjection = useMemo(() => {
    const proj = displayEconomyCodes.map((economyCode) => ({
      economyCode,
      centroid: [
        parseFloat(coord[economyCode].x),
        parseFloat(coord[economyCode].y),
      ],
      x: width / 2,
      y: height / 2,
    }));
    return proj as ((typeof proj)[number] & { fx?: number; fy?: number })[];
  }, [displayEconomyCodes]);

  const circleValueExtent = d3.extent(
    Object.values(importCircleSizes).concat(Object.values(exportCircleSizes))
  ) as number[];

  const circleRadius = d3
    .scalePow()
    .exponent(0.75)
    .domain(circleValueExtent)
    .range([10, maxCircleR]);

  const circleSizes = toggle === "i" ? importCircleSizes : exportCircleSizes;

  const getCircleR = (economyCode: keyof typeof coord) =>
    Math.round(circleRadius(circleSizes[economyCode]));

  const projection = d3.geoMercator().fitExtent(
    [
      [padding, padding],
      [width - padding, height - padding],
    ],
    {
      type: "MultiPoint",
      coordinates: geoProjection.map((node) => node.centroid),
    }
  );

  useEffect(() => {
    // Stop animation after a certain number of ticks
    if (animationTickCount > 100) return;

    const simulation = d3
      .forceSimulation(geoProjection)
      .force(
        "x",
        d3.forceX((d) => projection(d.centroid as [number, number])?.[0] || 0)
      )
      .force(
        "y",
        d3.forceY((d) => projection(d.centroid as [number, number])?.[1] || 0)
      )
      .force(
        "collide",
        d3.forceCollide((d) => 1 + getCircleR(d.economyCode) + 25)
      )
      .force("center", d3.forceCenter(width / 2, height / 2))
      .force("bound", () => {
        geoProjection.forEach((node) => {
          if (node.x < padding) {
            node.fx = padding;
          }

          if (node.x > width - padding) {
            node.fx = width - padding;
          }

          if (node.y < padding) {
            node.fy = padding;
          }

          if (node.y > height - padding) {
            node.fy = height - padding;
          }
        });
      });

    simulation.on("tick", () => {
      setAnimatedNodes([...simulation.nodes()]);
      setAnimationTickCount((prev) => prev + 1);
    });

    simulation.restart();

    return () => {
      simulation.stop();
    };
  });

  // Create scale for stroke width (using the extent of data values for all years (so that year to year changes are visibile))
  const maxValue = Math.max(...data.map((d) => d["Value"]));
  const minValue = Math.min(...data.map((d) => d["Value"]));
  const dataDomain = [minValue, maxValue];

  const dataRange = [3, 30]; // stroke width in pixels

  // Create function to use in JSX below
  // Use power scale to prevent outlier from being the only large arrow
  const sizePath = d3
    .scalePow()
    .exponent(0.75)
    .domain(dataDomain)
    .range(dataRange);

  const displaySector = getSectorDisplayName(sector);
  const displaySubSector = getSubSectorDisplayName(subSector);

  const sectorName =
    displaySubSector.toLocaleLowerCase() === "total"
      ? displaySector
      : displaySubSector;

  // This needs to be sorted so that the largest flows are drawn first and
  // smaller ones on top so that they can still be hovered
  const sortedData = data.sort((a, b) => b["Value"] - a["Value"]);

  const sortedAnimatedNodes = animatedNodes.sort(
    (a, b) => circleSizes[b.economyCode] - circleSizes[a.economyCode]
  );

  return (
    <div>
      <ChartHeader
        title={`Global trade flows for ${sectorName.toLowerCase()}`}
        description={`See how ${sectorName.toLowerCase()} moved in ${year} in the top 40 trade corridors by value, making up ${topFlowsPercent}% of trade.`}
      >
        <Toggle
          options={toggleOptions}
          label="Size each economy’s circle by"
          value={toggle}
          onValueChange={(value) => {
            if (value) {
              onToggleChange(value);
              setAnimationTickCount(0);
            }
          }}
        />
      </ChartHeader>

      {!sortedData.length ? (
        <NoData />
      ) : (
        <div className={styles["chart-wrap"]}>
          <p className={styles["tooltip-container"]}>
            {tooltipVisible && tooltipData?.type === "economy" ? (
              <span className={styles.tooltip}>
                <span className={styles["tooltip-title"]}>
                  Total {toggle === "i" ? "imports" : "exports"} of{" "}
                  {sectorName.toLowerCase()}
                  <br />
                  {toggle === "i" ? "to" : "from"}{" "}
                  {getEconomyDisplayName(tooltipData.code)}
                </span>
                <br />
                <span className={styles["tooltip-value"]}>
                  {formatMoney(circleSizes[tooltipData.code])}
                </span>
              </span>
            ) : tooltipVisible && tooltipData?.type === "flow" ? (
              <span className={styles.tooltip}>
                <span className={styles["tooltip-title"]}>
                  {sectorName} exported
                  <br />
                  from {getEconomyDisplayName(
                    tooltipData["Partner code"]
                  )} to {getEconomyDisplayName(tooltipData["Reporter code"])}
                </span>
                <br />
                <span className={styles["tooltip-value"]}>
                  {formatMoney(tooltipData["Value"])}
                </span>
              </span>
            ) : null}
          </p>
          {sortedAnimatedNodes.length ? (
            <span
              className={styles.hide}
            >{`The economy with the largest ${toggleOptions
              .find((o) => o.value === toggle)
              ?.label.toLowerCase()} is ${getEconomyDisplayName(
              sortedAnimatedNodes[0].economyCode
            )} with a value of ${formatMoneySr(
              circleSizes[sortedAnimatedNodes[0].economyCode]
            )}, followed by ${formatDisplayList(
              sortedAnimatedNodes
                .slice(1)
                .map(
                  (node) =>
                    `${getEconomyDisplayName(
                      node.economyCode
                    )} with a value of ${formatMoneySr(
                      circleSizes[node.economyCode]
                    )}`
                )
            )}.`}</span>
          ) : null}
          <svg viewBox={`0 0 ${width} ${height}`} aria-hidden>
            <defs>
              <filter id="textBackground" x="0" y="0" width="1" height="1">
                <feFlood floodColor="rgba(255,255,255,1)" result="bg" />
                <feMerge>
                  <feMergeNode in="bg" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
                <feComposite in="SourceGraphic" />
              </filter>
              <marker
                id="flow-arrow"
                viewBox="0 0 18 18"
                refX="0.2"
                refY="9"
                markerWidth="3"
                markerHeight="3"
                orient="auto-start-reverse"
              >
                <path d="M0 17V1L13 9L0 17Z" />
              </marker>
              <marker
                id="flow-arrow-small"
                viewBox="0 0 18 18"
                refX="0.2"
                refY="9"
                markerWidth="2"
                markerHeight="2"
                orient="auto-start-reverse"
              >
                <path d="M0 17V1L13 9L0 17Z" />
              </marker>
              <marker
                id="flow-arrow-large"
                viewBox="0 0 18 18"
                refX="0.2"
                refY="9"
                markerWidth="4.5"
                markerHeight="4.5"
                orient="auto-start-reverse"
              >
                <path d="M0 17V1L13 9L0 17Z" />
              </marker>
            </defs>

            <g className={styles["flow-paths"]}>
              {animatedNodes.length
                ? sortedData.map((flow) => {
                    const exporter = animatedNodes.find(
                      (node) => node.economyCode === flow["Partner code"]
                    );
                    const importer = animatedNodes.find(
                      (node) => node.economyCode === flow["Reporter code"]
                    );
                    const strokeWidth = sizePath(flow["Value"]);

                    if (!exporter || !importer) return null;

                    // Compute the path for the full arc between start and end points
                    const path = createPathString(exporter, importer);

                    // Shorten this path to make room for marker at endpoint
                    // Adapted from https://stackoverflow.com/questions/41226734/align-marker-on-node-edges-d3-force-layout/41229068#41229068
                    const pathLength = getLength(path);

                    // Attach a smaller arrow (marker) if the path is short to avoid crowding and attach a bigger arrow if the stroke width is small to avoid loosing the direction of the arrow
                    const arrowType =
                      strokeWidth < 7
                        ? "large"
                        : pathLength < 200
                        ? "small"
                        : "medium";

                    // To stop the line short of the circle's edge, we need to use the marker size (default or small), the strokeWidth (which scales the marker) as well as the circle radius
                    const markerSize =
                      arrowType === "small"
                        ? 1.25 * strokeWidth
                        : arrowType === "large"
                        ? 2.75 * strokeWidth
                        : 2.25 * strokeWidth;
                    const radius =
                      getCircleR(importer.economyCode) + markerSize;

                    // Set the distance the arrow should stop short of the circle edge
                    const offset = 10;

                    // Find a point at `radius - offset` distance from the end of the path
                    const modifiedEndpoint = getPointAtLength(
                      path,
                      pathLength - radius - offset
                    );

                    // Use modifiedEndpoint to compute the path for the shortened arc
                    const modifiedPath = createPathString(
                      exporter,
                      modifiedEndpoint
                    );

                    // Save another endpoint for centering the hoverable circle
                    const arrowHoverableEndpoint = getPointAtLength(
                      path,
                      pathLength - radius - offset + markerSize / 2
                    );

                    const isActive =
                      tooltipData?.type === "economy" &&
                      tooltipVisible &&
                      tooltipData?.code ===
                        (toggle === "i"
                          ? flow["Reporter code"]
                          : flow["Partner code"]);

                    return (
                      // Group flow line and transparent circles
                      <g
                        className={clsx(
                          styles["flow-path"],
                          isActive && styles["flow-path-active"]
                        )}
                        key={`${flow["Reporter code"]}-${flow["Partner code"]}`}
                        onMouseEnter={(event) => {
                          showTooltip(event, {
                            ...flow,
                            type: "flow",
                            relatedEconomies: [
                              flow["Reporter code"],
                              flow["Partner code"],
                            ],
                          });
                        }}
                        onMouseLeave={() => {
                          hideTooltip();
                        }}
                      >
                        {/* Transparent circle over end marker to capture hovers */}
                        <g
                          transform={`translate(${arrowHoverableEndpoint.x}, ${arrowHoverableEndpoint.y})`}
                        >
                          <circle
                            fill="transparent"
                            r={
                              arrowType === "small"
                                ? strokeWidth
                                : arrowType === "large"
                                ? strokeWidth * 2
                                : strokeWidth * 1.5
                            }
                          />
                        </g>

                        {/* Flow line (trade corridor) */}
                        <path
                          markerEnd={`url(${
                            arrowType === "small"
                              ? "#flow-arrow-small"
                              : arrowType === "large"
                              ? "#flow-arrow-large"
                              : "#flow-arrow"
                          })`}
                          d={modifiedPath}
                          strokeWidth={strokeWidth}
                          fill="none"
                        />
                      </g>
                    );
                  })
                : null}
            </g>
            <g className={styles["economy-nodes"]}>
              {animatedNodes.map((node) => {
                const r = getCircleR(node.economyCode);
                const isHighlighted =
                  tooltipData?.type === "economy" &&
                  tooltipVisible &&
                  tooltipData?.code === node.economyCode;
                const isRelated =
                  tooltipVisible &&
                  tooltipData?.relatedEconomies?.includes(node.economyCode);

                return (
                  <g
                    key={node.economyCode}
                    className={clsx(
                      styles["economy-node"],
                      (isHighlighted || isRelated) &&
                        styles["economy-node-active"]
                    )}
                    transform={`translate(${node.x}, ${node.y})`}
                    onMouseEnter={(event) => {
                      const relatedEconomies = sortedData.reduce<string[]>(
                        (relatedEconomies, flow) => {
                          if (
                            toggle === "i" &&
                            flow["Reporter code"] === node.economyCode
                          ) {
                            relatedEconomies.push(flow["Partner code"]);
                          } else if (
                            toggle === "e" &&
                            flow["Partner code"] === node.economyCode
                          ) {
                            relatedEconomies.push(flow["Reporter code"]);
                          }
                          return relatedEconomies;
                        },
                        []
                      );

                      showTooltip(event, {
                        code: node.economyCode,
                        type: "economy",
                        relatedEconomies,
                      });
                    }}
                    onMouseLeave={() => {
                      hideTooltip();
                    }}
                    style={
                      {
                        "--region-color":
                          colors[
                            regionColors[economyRegions[node.economyCode]]
                          ],
                      } as React.CSSProperties
                    }
                  >
                    <text
                      filter={
                        isHighlighted || isRelated ? `url(#textBackground)` : ``
                      }
                      pointerEvents="none"
                      textRendering="optimizeLegibility"
                      transform={`translate(0,${r + 20})`}
                    >
                      {getEconomyDisplayName(node.economyCode)}
                    </text>
                    <circle r={r} stroke="white" strokeWidth="2" />
                  </g>
                );
              })}
            </g>
          </svg>

          <div className={styles.legend}>
            <div className={styles["legend-colors"]}>
              {legendItems && <ColorLegend items={legendItems} />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
